import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
    createHourLogEmployee,
    listWorkgroups, listWorkgroupMembers,
} from "../../lib/api";

export default function NewEmployeeDialog({ open, onClose, config, onCreated }) {
    const [tab, setTab] = useState("ghost");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [rate, setRate] = useState("");
    const [wgs, setWgs] = useState([]);
    const [wgId, setWgId] = useState(config?.workgroup_id || "");
    const [members, setMembers] = useState([]);
    const [picked, setPicked] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const list = await listWorkgroups();
                setWgs(list || []);
                const target = config?.workgroup_id || list?.[0]?.id || "";
                setWgId(target);
            } catch { /* ignore */ }
        })();
    }, [config?.workgroup_id]);

    useEffect(() => {
        if (!wgId) return;
        (async () => {
            try {
                const m = await listWorkgroupMembers(wgId);
                setMembers(m || []);
            } catch { setMembers([]); }
        })();
    }, [wgId]);

    const create = async () => {
        setBusy(true);
        try {
            const rateNum = rate ? Number(rate) : null;
            if (tab === "member" && picked) {
                await createHourLogEmployee({
                    name: picked.name || picked.email || "Member",
                    email: picked.email || "",
                    member_user_id: picked.user_id,
                    workgroup_id: wgId || null,
                    hourly_rate: rateNum,
                });
            } else {
                if (!name.trim()) { toast.error("Name required"); return; }
                await createHourLogEmployee({
                    name: name.trim(),
                    email: email.trim() || null,
                    hourly_rate: rateNum,
                });
            }
            await onCreated();
            onClose();
        } catch {
            toast.error("Couldn't add employee");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="bg-[#131A2F] border border-[#2A3655] text-white max-w-lg"
                data-testid="new-employee-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">Add employee</DialogTitle>
                </DialogHeader>
                <div className="flex gap-1 mb-3">
                    <button
                        onClick={() => setTab("member")}
                        className={`flex-1 px-3 py-2 rounded-sm text-[11px] uppercase tracking-wider font-bold transition-colors ${tab === "member" ? "bg-[#00A3FF] text-[#0A1128]" : "bg-[#1C253C] text-slate-300 hover:bg-[#223055]"}`}
                        data-testid="tab-from-workgroup"
                    >From workgroup</button>
                    <button
                        onClick={() => setTab("ghost")}
                        className={`flex-1 px-3 py-2 rounded-sm text-[11px] uppercase tracking-wider font-bold transition-colors ${tab === "ghost" ? "bg-[#00A3FF] text-[#0A1128]" : "bg-[#1C253C] text-slate-300 hover:bg-[#223055]"}`}
                        data-testid="tab-ghost"
                    >Ghost (no account)</button>
                </div>
                {tab === "member" ? (
                    <div className="space-y-2">
                        <select
                            value={wgId}
                            onChange={(e) => setWgId(e.target.value)}
                            data-testid="select-workgroup"
                            className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm"
                        >
                            <option value="">Select a workgroup</option>
                            {wgs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                        <div className="max-h-72 overflow-auto bg-[#0A1128] border border-[#2A3655] rounded-sm">
                            {members.map((m) => (
                                <button
                                    key={m.user_id}
                                    onClick={() => setPicked(m)}
                                    data-testid={`pick-member-${m.user_id}`}
                                    className={`w-full text-left px-3 py-2 border-b border-[#2A3655] last:border-0 hover:bg-[#1C253C] ${picked?.user_id === m.user_id ? "bg-[#00A3FF]/15" : ""}`}
                                >
                                    <div className="text-sm text-white">{m.name || m.email}</div>
                                    {m.email && <div className="text-[11px] text-slate-400">{m.email}</div>}
                                </button>
                            ))}
                            {members.length === 0 && <div className="px-3 py-4 text-xs text-slate-400">No members.</div>}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="ghost-name" className="bg-[#0A1128] border-[#2A3655]" />
                        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" data-testid="ghost-email" className="bg-[#0A1128] border-[#2A3655]" />
                        <div className="text-[11px] text-slate-500">Ghost employees don't get app access — perfect for crew members who only need hours tracked.</div>
                    </div>
                )}
                <div className="pt-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Hourly rate (optional)</label>
                    <div className="relative mt-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                        <Input
                            type="number"
                            min="0"
                            step="0.25"
                            value={rate}
                            onChange={(e) => setRate(e.target.value)}
                            placeholder="0.00"
                            data-testid="employee-rate"
                            className="bg-[#0A1128] border-[#2A3655] pl-6"
                        />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Used to compute total pay on the payroll report.</div>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="px-4 py-2 text-xs uppercase font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white">Cancel</button>
                    <button onClick={create} disabled={busy} data-testid="btn-create-employee" className="px-4 py-2 text-xs uppercase font-bold rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] hover:bg-[#00A3FF]/30 disabled:opacity-50">
                        {busy ? "Adding…" : "Add"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
